import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowUpIcon } from "@heroicons/react/24/solid";
import { ClockIcon } from "@heroicons/react/24/outline";
import { Markdown } from "@areas/chatbot/utils";
import {
  useAutocompleteSuggestions,
  useSuggestionCatalogue,
  useSuggestionEvents,
  useSuggestionSlots,
} from "@api/intelligenceController";
import { useHistoricalAnalysisMcp, useAiResponseTimer, useCatalogueAutocomplete } from "@flags";
import { cn } from "@utils";
import Tooltip from "@components/Tooltip";
import { SINGLE_LINE_TOOLTIP } from "./documentActions";
import MentionTextarea from "../SmartNoteModal/MentionTextarea";
import AiSpinner from "../SmartNoteModal/AiSpinner";
import AiSparkleIcon from "../SmartNoteModal/AiSparkleIcon";
import DraftSuggestionCard from "../SmartNoteModal/DraftSuggestionCard";
import SuggestionComponentCard from "../SmartNoteModal/SuggestionComponentCard";
import HistoricalSuggestionCard from "../SmartNoteModal/HistoricalSuggestionCard";
import ChartCard from "../SmartNoteModal/ChartCard";
import { getSuggestionComponent } from "../SmartNoteModal/suggestionComponents";
import SuggestionPanel, { useSuggestionLoader } from "./SuggestionPanel";
import { matchCatalogue, renderCatalogue } from "./suggestionCatalogue";

const PROMPT_PLACEHOLDER =
  "You can add a note, send a request or type @ to notify a colleague";
const READ_LOADING_PLACEHOLDER = "How can I help you today?";
const THINKING_PLACEHOLDER = "Ontik is thinking…";

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function PromptText({ text, mentionNames = [] }) {
  if (mentionNames.length === 0) return text;
  const tokens = new Set(mentionNames.map((name) => `@${name}`));
  const pattern = [...tokens]
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join("|");
  return text.split(new RegExp(`(${pattern})`, "g")).map((part, i) =>
    tokens.has(part) ? (
      <span key={i} className="font-medium text-blue-600">
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

function AssistantReply({ text, onGrow }) {
  useEffect(() => {
    onGrow?.();
  }, [text, onGrow]);
  return (
    <div className="animate-fadeIn w-full text-sm text-zinc-800">
      <Markdown>{text}</Markdown>
    </div>
  );
}

export function DiscoverButton({ onClick, className }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white py-1.5 pl-1.5 pr-2.5 text-xs font-semibold text-zinc-600 shadow-sm transition-colors hover:border-zinc-300 hover:text-zinc-900",
        className,
      )}
    >
      <AiSparkleIcon className="size-4" />
      Discover
    </button>
  );
}

// The left column of the account view: a scrolling greeting + conversation thread,
// with the composer pinned to the bottom. The suggestion cards and all accept/reject behaviour are
// driven entirely by the shared controller, so this is a pure presentation of its state.
export default function ConversationColumn({
  controller: c,
  customer,
  greeting,
  readRevealed,
  historyOpen,
  onToggleHistory,
  composerRef,
}) {
  const chartsEnabled = useHistoricalAnalysisMcp();
  const responseTimerEnabled = useAiResponseTimer();

  const scrollRef = useRef(null);
  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);
  useEffect(() => {
    scrollToBottom();
  }, [c.messages, c.isThinking, scrollToBottom]);

  const hasThread = c.messages.length > 0 || c.isThinking;
  // A reopened past thread carries historical turns; the greeting is hidden for it.
  const isReopened = c.messages.some((m) => m.historical);

  // Composer autocomplete — the "Suggested" prompts. Kept available the whole time the user is composing:
  // on the fresh screen and for follow-ups, empty or while typing (which narrows the list). Only hidden
  // while a reply is streaming or when viewing a read-only reopened thread. The arrow keys pick an
  // "active" row (hover never does): Enter sends it, Tab fills the composer with it, and it ghosts into the composer.
  const composerText = c.draft?.text ?? "";
  const [activeIndex, setActiveIndex] = useState(-1); // -1 = nothing highlighted
  useEffect(() => {
    setActiveIndex(-1);
  }, [customer.id]);
  // Typing (which narrows the list) drops any highlight.
  useEffect(() => {
    setActiveIndex(-1);
  }, [composerText]);

  // Fetch as soon as the read is ready (during its reveal animation) so suggestions are in hand the moment
  // it lands, but only reveal the panel once the read has finished animating in. Kept visible while a reply
  // streams too — sending resets the composer to empty, so the panel just resets to fresh starters rather
  // than disappearing and reappearing.
  const autocompleteActive = !isReopened && c.readReady;
  const showAutocomplete = autocompleteActive && readRevealed;
  // Two arms behind a flag: the curated catalogue (fetched on open, matched in the browser on every
  // keystroke) or the per-keystroke model.
  const catalogueEnabled = useCatalogueAutocomplete();
  const { catalogue } = useSuggestionCatalogue({
    customerId: customer.id,
    enabled: catalogueEnabled && !isReopened,
  });
  const { slotBag } = useSuggestionSlots({
    customerId: customer.id,
    enabled: catalogueEnabled && !isReopened,
  });
  const candidates = useMemo(() => renderCatalogue(catalogue, slotBag), [catalogue, slotBag]);
  const catalogueSuggestions = useMemo(
    () => matchCatalogue(candidates, composerText, 3),
    [candidates, composerText],
  );
  const { suggestions: modelSuggestions, isFetching: modelFetching } = useAutocompleteSuggestions({
    customerId: customer.id,
    prefix: composerText,
    count: 3,
    enabled: autocompleteActive && !catalogueEnabled,
  });
  const fetchedSuggestions = catalogueEnabled ? catalogueSuggestions : modelSuggestions;
  const loaderMode = useSuggestionLoader(!catalogueEnabled && modelFetching, fetchedSuggestions.length === 0);
  // While the first set generates, rows that land are held back until the loader hands over to them.
  const suggestions = loaderMode === "generating" ? [] : fetchedSuggestions;

  const activeSuggestion = activeIndex >= 0 ? suggestions[activeIndex] : null;
  const suggestionsShown = showAutocomplete && (suggestions.length > 0 || loaderMode === "generating");

  // Usage events for ranking and the arm comparison. An impression is a row left on screen for 500ms,
  // counted once per customer, so rows flashing past while typing don't dilute the acceptance rate.
  const logSuggestionEvent = useSuggestionEvents(customer.id);
  const suggestionEvent = useCallback(
    (suggestion, type) => {
      const catalogueRow = suggestion.source === "catalogue";
      logSuggestionEvent({
        suggestionId: catalogueRow ? suggestion.id : null,
        source: catalogueRow ? "catalogue" : "model",
        type,
        position: Math.max(suggestions.indexOf(suggestion), 0),
      });
    },
    [logSuggestionEvent, suggestions],
  );
  const impressed = useRef(new Set());
  useEffect(() => {
    impressed.current = new Set();
  }, [customer.id]);
  useEffect(() => {
    if (!showAutocomplete || suggestions.length === 0) return undefined;
    const timer = setTimeout(() => {
      suggestions.forEach((suggestion) => {
        const key = `${suggestion.source ?? "model"}:${suggestion.id ?? suggestion.promptText}`;
        if (impressed.current.has(key)) return;
        impressed.current.add(key);
        suggestionEvent(suggestion, "Impression");
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [showAutocomplete, suggestions, suggestionEvent]);

  // Click a row, or Enter on the highlighted row → send it (like pressing Enter).
  const sendSuggestion = useCallback(
    (suggestion) => {
      suggestionEvent(suggestion, "Accept");
      c.sendAutocompleteSuggestion(suggestion.promptText);
    },
    [c, suggestionEvent],
  );
  const acceptActiveSuggestion = useCallback(() => {
    if (!showAutocomplete || !suggestions[activeIndex]) return false;
    sendSuggestion(suggestions[activeIndex]);
    return true;
  }, [showAutocomplete, suggestions, activeIndex, sendSuggestion]);

  // Tab → fill the composer with the highlighted row (or the first) so it can be edited before sending.
  const fillFromSuggestions = useCallback(() => {
    if (!showAutocomplete || suggestions.length === 0) return false;
    const index = activeIndex >= 0 ? activeIndex : 0;
    suggestionEvent(suggestions[index], "Fill");
    c.acceptAutocompleteSuggestion(suggestions[index].promptText);
    return true;
  }, [showAutocomplete, suggestions, activeIndex, c, suggestionEvent]);

  // Arrow down/up moves the highlight; arrowing up past the top hands focus back to the composer (-1).
  const navigateSuggestions = useCallback(
    (delta) => {
      if (!showAutocomplete || suggestions.length === 0) return false;
      if (delta < 0 && activeIndex <= -1) return false;
      setActiveIndex(Math.min(Math.max(activeIndex + delta, -1), suggestions.length - 1));
      return true;
    },
    [showAutocomplete, suggestions.length, activeIndex],
  );

  // Escape clears the highlight, reverting the composer to what the user actually typed.
  const cancelSuggestion = useCallback(() => {
    if (activeIndex < 0) return false;
    setActiveIndex(-1);
    return true;
  }, [activeIndex]);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col bg-zinc-50">
      <div
        ref={scrollRef}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-4 pt-4"
      >
        <div className="flex items-center">
          <Tooltip
            content={historyOpen ? "Hide history" : "Show history"}
            placement="right"
            wrapperClassName="flex"
            className={SINGLE_LINE_TOOLTIP}
          >
            <button
              type="button"
              onClick={onToggleHistory}
              aria-label={historyOpen ? "Hide history" : "Show history"}
              aria-pressed={historyOpen}
              className={cn(
                "flex size-8 items-center justify-center rounded-lg transition-colors",
                historyOpen
                  ? "bg-zinc-200 text-zinc-700"
                  : "text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600",
              )}
            >
              <ClockIcon aria-hidden="true" className="size-5" />
            </button>
          </Tooltip>
          <DiscoverButton onClick={c.openHelp} className="ml-auto" />
        </div>

        {/* mt-auto drops the greeting + thread to the bottom of the column (just above the
            composer) when they're short — matching the designs — and collapses to a normal top-down
            scroll once a conversation grows past the available height. */}
        <div className="mt-auto">
          {!isReopened && (
            <p className="text-sm text-zinc-800">
              {greeting}! What would you like to do with {customer.name}?
            </p>
          )}

          {hasThread && (
            <div className="mt-6 flex w-full flex-col gap-4">
            {c.messages.map((message) =>
              message.role === "user" ? (
                <div key={message.id} className="flex flex-col items-end gap-1">
                  <div className="max-w-[85%] rounded-2xl bg-white px-4 py-2.5 text-sm text-zinc-900 shadow-sm">
                    <PromptText
                      text={message.text}
                      mentionNames={(message.mentions ?? []).map((m) => m.name)}
                    />
                  </div>
                </div>
              ) : (
                <div key={message.id} className="flex flex-col gap-3">
                  {message.reply && (
                    <AssistantReply
                      text={message.reply}
                      onGrow={message.signals?.length ? undefined : scrollToBottom}
                    />
                  )}
                  {chartsEnabled &&
                    (message.visualizations ?? []).map((chart) => (
                      <div key={chart.id} className="animate-fadeIn">
                        <ChartCard spec={chart} />
                      </div>
                    ))}
                  {message.historical &&
                    message.signals.map((s, i) => (
                      <HistoricalSuggestionCard
                        key={`${message.id}:${i}`}
                        suggestion={s}
                        customer={customer}
                      />
                    ))}
                  {!message.historical &&
                    message.signals.map((s, i) => {
                    const descriptor = getSuggestionComponent(s.type);
                    return descriptor ? (
                      <SuggestionComponentCard
                        key={`${message.id}:${i}`}
                        suggestion={s}
                        descriptor={descriptor}
                        index={i}
                        isSending={c.pending.has(`${message.id}:${i}`)}
                        isDone={c.done.has(`${message.id}:${i}`)}
                        isRejected={c.rejected.has(`${message.id}:${i}`)}
                        onAccept={(values) =>
                          c.handleAcceptComponent(message, i, s, descriptor, values)
                        }
                        onReject={() => c.handleRejectSuggestion(message, i, s)}
                        onGrow={scrollToBottom}
                      />
                    ) : (
                      <DraftSuggestionCard
                        key={`${message.id}:${i}`}
                        suggestion={s}
                        index={i}
                        isSending={c.pending.has(`${message.id}:${i}`)}
                        isDone={c.done.has(`${message.id}:${i}`)}
                        isRejected={c.rejected.has(`${message.id}:${i}`)}
                        onAccept={(content, mentions) =>
                          c.handleAcceptSuggestion(message, i, s, content, mentions)
                        }
                        onReject={() => c.handleRejectSuggestion(message, i, s)}
                        onEdit={() => c.handleEditSuggestion(message, i, s)}
                        taggedUsers={message.mentions ?? []}
                        customer={c.analysedCustomer}
                        onGrow={scrollToBottom}
                        scrollContainerRef={scrollRef}
                        onPreviewStep={(step, instalment) => c.setPreview({ step, instalment })}
                      />
                    );
                  })}
                </div>
              ),
            )}

            {c.isThinking &&
              !(
                c.messages[c.messages.length - 1]?.role === "assistant" &&
                (c.messages[c.messages.length - 1]?.reply ||
                  c.messages[c.messages.length - 1]?.signals?.length)
              ) && (
                <div className="flex items-center gap-2 text-sm text-zinc-400" aria-live="polite">
                  <AiSpinner size={16} />
                  <span key={c.thinkingLabel ?? THINKING_PLACEHOLDER} className="animate-fadeIn">
                    {c.thinkingLabel ?? THINKING_PLACEHOLDER}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="px-6 pb-6 pt-2">
        <div className="w-full">
          {responseTimerEnabled && c.lastCardMs != null && (
            <div className="mb-2 inline-flex self-start rounded-full bg-zinc-900 px-2.5 py-1 font-mono text-xs text-white">
              ⏱ suggestion shown in {(c.lastCardMs / 1000).toFixed(2)}s
            </div>
          )}
          <div
            ref={composerRef}
            className="relative rounded-2xl border border-zinc-300 bg-white p-3 shadow-sm"
          >
            <div className="group/input relative flex items-end gap-3">
              {/* Focus darkens the card's own edges around the input — top, sides and the divider above the
                  suggestions (or the card's bottom when there are none) — rather than the whole card. */}
              <span
                aria-hidden="true"
                className={cn(
                  "pointer-events-none absolute -inset-[13px] border border-zinc-400 opacity-0 transition-opacity group-focus-within/input:opacity-100",
                  suggestionsShown ? "rounded-t-2xl" : "rounded-2xl",
                )}
              />
              <div className="min-w-0 flex-1">
                <MentionTextarea
                  key={c.editorKey}
                  bare
                  popoverPlacement="above"
                  placeholder={PROMPT_PLACEHOLDER}
                  initialText={c.composerSeed?.text ?? ""}
                  onChange={c.handleDraftChange}
                  onCustomerSelect={c.setSelectedCustomer}
                  selectedCustomer={c.selectedCustomer}
                  onSubmit={c.handleSubmit}
                  onTab={fillFromSuggestions}
                  onSuggestionArrow={navigateSuggestions}
                  onSuggestionEnter={acceptActiveSuggestion}
                  onSuggestionEscape={cancelSuggestion}
                  activePreview={showAutocomplete && activeSuggestion ? activeSuggestion.promptText : null}
                  readLoading={!isReopened && Boolean(c.selectedCustomer) && !c.readReady}
                  loadingPlaceholder={READ_LOADING_PLACEHOLDER}
                />
              </div>
              <button
                type="button"
                onClick={c.handleSubmit}
                disabled={!c.canSubmit}
                aria-label="Send"
                className="-my-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary-400 text-white transition-colors hover:bg-primary-500 active:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:bg-primary-200"
              >
                <ArrowUpIcon aria-hidden="true" className="size-4" />
              </button>
            </div>

            {/* The composer grows into the suggestions rather than jumping: a grid-rows 0fr→1fr keyframe
                animates the height open when the panel mounts (once suggestions are actually in hand — no
                empty pop), and the rows fade in at the read's cadence, so it reads as one wave flowing down.
                A keyframe (not a transition) is used deliberately: the panel often mounts with suggestions
                already fetched, so there is no prior 0fr frame for a transition to animate from. */}
            {suggestionsShown && (
              <div className="grid animate-suggestionGrow motion-reduce:animate-none">
                <div className="overflow-hidden">
                  <SuggestionPanel
                    suggestions={suggestions}
                    activeIndex={activeIndex}
                    onAccept={sendSuggestion}
                    loaderMode={loaderMode}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
